///
/// EarthBrowser
///
/// Created by Matt Giger on 9/14/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Global radar mosaic overlay. Frames are global equirectangular R8
/// WebPs (0 = nodata, 1..255 = -10..80 dBZ) composited server-side from
/// the tier-1 national radar networks (MRMS, OPERA, GeoMet, JMA, KMA,
/// CWA). Reuses the overlay_radar shader chunk with full-globe bounds,
/// so the CONUS remap in the shader becomes an identity mapping.
///
/// Frames are advected along estimated optical flow while blending (see
/// PrecipFlowTextureSeries), like the IMERG and cloud overlays, so echoes
/// translate with moving systems between 5-minute slots instead of
/// cross-dissolving in place - a dissolve between near-identical frames
/// reads as a frozen layer next to the flow-advected ones.
///
/// Where a 3D NEXRAD volume is drawing, the mosaic (and its refinement
/// tiles) step back to a gray footprint (datasets/RadarVolumeMask.ts) so
/// the reflectivity color is only said once at any location.
///

import { TextureOverlay } from "@/earthbrowser/datasets/TextureOverlay"
import { precipAltitudeScale, radarMosaicScale } from "@/earthbrowser/datasets/precipAltitude"
import { PrecipFlowTextureSeries } from "@/earthbrowser/dataset-distribution/imerg/PrecipFlowTextureSeries"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { wgs } from "@/earthbrowser/utils/geographic"
import { type Material } from "@stovokor/shader/Material"
import { type RenderState } from "@stovokor/engine/RenderState"
import { clamp } from "@stovokor/math/math"
import { type matrix } from "@stovokor/math/matrix"
import { type Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { vec4 } from "@stovokor/math/vector"

// Full globe in normalized texture coordinates: [minU, minV, maxU, maxV]
const cGlobalBounds = new vec4(0.0, 0.0, 1.0, 1.0)
// Wall-clock fade of the mosaic hand-off as refinement tiles start or stop drawing
const cHandoffSeconds = 0.5

export class RadarOverlay extends TextureOverlay {
	private flowSeries: PrecipFlowTextureSeries
	private ebContext: EarthBrowserContext
	/** Whether Web Mercator refinement tiles are currently drawing over the
	 *  globe (RadarDatasetLayer wires this to the tile cache). The mosaic
	 *  only yields to them close in while this is true */
	public refinementRendering: () => boolean = () => false
	/** 0..1 share of the picture handed to the forecast radar past the
	 *  newest observation (RadarForecastOverlay.weight while that layer is
	 *  on). The mosaic fades out by it instead of holding its last frame
	 *  under the forecast echoes */
	public yieldToForecast: () => number = () => 0
	// Smoothed 0..1 form of refinementRendering, shared by every tile's
	// material so the hand-off doesn't pop as tiles come and go
	private handoff = 0
	private lastHandoffMs = 0

	constructor(context: EarthBrowserContext) {
		super(context)
		this.ebContext = context
		this.flowSeries = new PrecipFlowTextureSeries(context)
		// One RG texture for the frame pair: see TextureSeries.packPairs
		this.flowSeries.packPairs = true
		this.textureSeries = this.flowSeries
	}

	public overlayMaterial(): Material {
		return new RadarOverlayMaterial(this.ebContext, this.flowSeries, () => this.handoff, () => this.yieldToForecast())
	}

	/** Flow field for the slot pair currently blending (equirect UV
	 *  displacement, see PrecipFlowTextureSeries), or undefined until it is
	 *  estimated. The refinement tiles blend the same pair, so they advect
	 *  along this field too instead of cross-dissolving below the hand-off */
	public get flowTexture(): Texture | undefined {
		return this.flowSeries.pairFlowTexture
	}

	/** Observation time (ms) of the newest mosaic frame, undefined with no frames */
	public get latestFrameMs(): number | undefined {
		const times = this.flowSeries.frameTimesMs()
		return times.length > 0 ? times[times.length - 1] : undefined
	}

	public override frameUpdate(state: RenderState): void {
		super.frameUpdate(state)
		const nowMs = performance.now()
		const dt = this.lastHandoffMs > 0 ? Math.min((nowMs - this.lastHandoffMs) / 1000, 0.1) : 0
		this.lastHandoffMs = nowMs
		const step = dt / cHandoffSeconds
		this.handoff = clamp(this.handoff + (this.refinementRendering() ? step : -step), 0, 1)
	}
}

class RadarOverlayMaterial implements Material {
	public shaderDefines: Record<string, string> = {
		"RADAR_TEXTURES": "1"
	}
	public available: boolean = true
	public transparency: boolean = false
	public radarFade = new UniformDynamic<number>()
	public radarBlend = new UniformDynamic<number>()
	public radarPair = new UniformDynamic<Texture>()
	public radarFlow = new UniformDynamic<Texture>()
	public radarBounds = new UniformDynamic<vec4>()
	// Echo opacity ramp and the hand-off to the refinement tiles, both by
	// camera altitude (datasets/precipAltitude.ts)
	public radarAltitudeScale = new UniformDynamic<number>()
	public radarMosaicScale = new UniformDynamic<number>()
	// Footprints of the drawing 3D volumes (radar_common.glsl)
	public radarVolumeMask = new UniformDynamic<matrix>()
	public radarVolumeMaskCount = new UniformDynamic<number>()

	private context: EarthBrowserContext
	private textureSeries: PrecipFlowTextureSeries
	private handoff: () => number
	private forecastYield: () => number

	constructor(context: EarthBrowserContext, textureSeries: PrecipFlowTextureSeries, handoff: () => number, forecastYield: () => number) {
		this.context = context
		this.textureSeries = textureSeries
		this.handoff = handoff
		this.forecastYield = forecastYield
	}

	public renderUpdate(state: RenderState): void {
		const altitudeKm = state.camera.position.length - wgs.a
		// Past the newest observation the forecast overlay takes the picture
		this.radarFade.value = this.textureSeries.fadeFactor * (1 - this.forecastYield())
		this.radarBlend.value = this.textureSeries.blendFactor
		this.radarPair.value = this.textureSeries.pairTexture
		this.radarFlow.value = this.textureSeries.flowTexture
		this.radarBounds.value = cGlobalBounds
		this.radarAltitudeScale.value = precipAltitudeScale(altitudeKm)
		// The mosaic fades out close in only to the extent the refinement
		// tiles have taken over; with none drawing it stays at full strength
		this.radarMosaicScale.value = 1 - (1 - radarMosaicScale(altitudeKm)) * this.handoff()
		this.radarVolumeMask.value = this.context.radarVolumeMask.mask
		this.radarVolumeMaskCount.value = this.context.radarVolumeMask.count
	}
}
